import {
  Text,
  IconBanknote,
  BorderRadius6,
  GreyGrey10,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  Primary12Regular,
  Primary13Regular,
  Primary13SemiBold,
  Totals,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { CustomerLogoCircle, NodeTimeline } from 'Automations/components'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { match } from 'ts-pattern'
import { ReactNode, useMemo, useState } from 'react'
import {
  dateTimeWithFormat,
  formatScheduleEndDate,
  toPriceSummary
} from '@sequencehq/utils'
import { PhaseBar } from 'BillingSchedules/index'
import {
  ExtractedBillingSchedule,
  TaskInputMinimum,
  TaskInputPhase,
  TaskInputPrice
} from './types'

export const ExtractedBillingScheduleDataTimelineItem = ({
  billingSchedule,
  isLatestNodeExecution,
  fileName,
  customer,
  timestamp,
  action,
  showPhaseBar = false,
  showPreview = true
}: {
  billingSchedule: ExtractedBillingSchedule
  isLatestNodeExecution: boolean
  timestamp?: string
  fileName: string
  customer?: {
    legalName?: string
    domain?: string
  }
  action?: ReactNode
  showPhaseBar?: boolean
  showPreview?: boolean
}) => {
  return (
    <NodeTimeline.Item
      icon={<IconBanknote />}
      showStem={!isLatestNodeExecution}
    >
      <NodeTimeline.Title>
        <NodeTimeline.UserAction
          label="Pricing information extracted"
          timestamp={timestamp}
        />
      </NodeTimeline.Title>
      {showPreview && (
        <ErrorBoundary name="ExtractedBillingScheduleDataTimelineItem">
          <ContractPricingPreview
            billingSchedule={billingSchedule}
            fileName={fileName}
            customer={customer}
            action={action}
            showPhaseBar={showPhaseBar}
          />
        </ErrorBoundary>
      )}
    </NodeTimeline.Item>
  )
}

const isPriceInsideMinimum = ({
  price,
  minimum
}: {
  price: TaskInputPrice
  minimum: TaskInputPhase['minimums'][0]
}): boolean => {
  if (!minimum) return false

  const scope = minimum.restrictToPrices.length ? 'specific' : 'allUsage'
  return match(scope)
    .with('allUsage', () => 'usageMetricId' in price.structure)
    .with('specific', () =>
      minimum.restrictToPrices.includes(price.temporaryPriceId)
    )
    .exhaustive()
}

const getPhaseLabel = (phase: {
  name?: string
  startDate?: string
  endDate?: string
}): string => {
  if (phase.name) return phase.name

  if (phase.startDate && phase.endDate) {
    return `${phase.startDate} - ${phase.endDate}`
  }

  return 'Open ended'
}

const getGroupByPhase = ({
  phases,
  prices
}: {
  phases: Array<TaskInputPhase>
  prices: Array<TaskInputPrice>
}): Array<{
  phaseId: string
  label: string
  subTotal?: number
  prices: Array<
    TaskInputPrice & {
      id: string
    }
  >
  minimum?: TaskInputMinimum & {
    prices: Array<
      TaskInputPrice & {
        id: string
      }
    >
  }
}> => {
  return phases.map(phase => {
    const minimum = phase.minimums.at(0)
    const phasePrices = phase.priceIds
      .map(priceId => prices.find(price => price.temporaryPriceId === priceId))
      .filter((price): price is TaskInputPrice => Boolean(price))

    const pricesOutsideMinimum = minimum
      ? phasePrices.filter(price => !isPriceInsideMinimum({ price, minimum }))
      : phasePrices

    const pricesInsideMinimum = minimum
      ? phasePrices.filter(price => isPriceInsideMinimum({ price, minimum }))
      : []

    return {
      phaseId: crypto.randomUUID(),
      label: getPhaseLabel(phase),
      prices: pricesOutsideMinimum
        .map(price => ({
          ...price,
          id: price.temporaryPriceId,
          productId: price.productId,
          name: price.name
        }))
        .map(p => ({
          ...p,
          summary: toPriceSummary(p)
        })),
      minimum: minimum && {
        ...minimum,
        prices:
          pricesInsideMinimum.length > 0
            ? pricesInsideMinimum
                .map(price => ({
                  ...price,
                  id: price.temporaryPriceId,
                  productId: price.productId,
                  name: price.name
                }))
                .map(p => ({
                  ...p,
                  summary: toPriceSummary(p)
                }))
            : []
      }
    }
  })
}

export const ContractPricingPreview = ({
  billingSchedule,
  fileName,
  customer,
  action,
  showPhaseBar = false,
  startCollapsed = false
}: {
  billingSchedule?: ExtractedBillingSchedule
  fileName: string
  customer?: {
    legalName?: string
    domain?: string
  }
  action?: ReactNode
  showPhaseBar?: boolean
  startCollapsed?: boolean
}) => {
  const [collapsed, setCollapsed] = useState(startCollapsed)
  const currency = billingSchedule?.prices?.[0]?.currency ?? 'USD'
  const groupByPhaseData = useMemo(() => {
    try {
      return getGroupByPhase({
        phases: billingSchedule?.phases ?? [],
        prices: billingSchedule?.prices ?? []
      })
    } catch {
      return null
    }
  }, [billingSchedule])

  const phaseBarPhases = useMemo(() => {
    return (billingSchedule?.phases ?? []).map((phase, index) => ({
      id: String(index),
      name: phase.name,
      startDate: phase.startDate,
      endDate: phase.endDate
    }))
  }, [billingSchedule?.phases])

  const scheduleDateRange = useMemo(() => {
    const phases = billingSchedule?.phases ?? []
    if (phases.length === 0) return null
    const startDate = phases[0]?.startDate
    const endDate = phases[phases.length - 1]?.endDate
    if (!startDate) return null
    return { startDate, endDate }
  }, [billingSchedule?.phases])

  if (!groupByPhaseData) {
    return (
      <Box {...Primary13SemiBold} color={GreyGrey80}>
        We couldn't find any pricing information in the document, but you can
        still create a customer and schedule.
      </Box>
    )
  }

  if (collapsed) {
    return (
      <Flex
        padding="8px"
        backgroundColor={GreyWhite}
        border={`1px solid ${GreyGrey30}`}
        borderRadius={BorderRadius6}
        onClick={() => setCollapsed(false)}
        cursor="pointer"
      >
        <Grid
          templateColumns={customer?.domain ? 'auto 1fr' : '1fr'}
          width="100%"
          columnGap="8px"
          alignItems="center"
          padding="3px 4px"
        >
          {customer?.domain && (
            <CustomerLogoCircle
              customer={{ domain: customer.domain }}
              size={16}
            />
          )}
          <Text {...Primary13SemiBold} color={GreyGrey70}>
            {fileName}
          </Text>
        </Grid>
      </Flex>
    )
  }

  return (
    <Flex
      padding="8px"
      backgroundColor={GreyWhite}
      border={`1px solid ${GreyGrey30}`}
      borderRadius={BorderRadius6}
      flexDir="column"
      gap="8px"
    >
      <Grid
        templateColumns={customer?.domain ? 'auto 1fr' : '1fr'}
        width="100%"
        templateRows="auto auto"
        padding="4px"
        paddingBottom="0"
        columnGap="12px"
        alignItems="center"
        onClick={() => setCollapsed(true)}
        cursor="pointer"
      >
        {customer?.domain && (
          <CustomerLogoCircle
            customer={{ domain: customer.domain }}
            size={32}
          />
        )}
        <Flex flexDir="column" gap="4px">
          <Box {...Primary13SemiBold} color={GreyGrey80}>
            {fileName}
          </Box>
          <Box {...Primary13Regular} color={GreyGrey70}>
            {customer?.legalName ?? 'Unknown Customer'}
          </Box>
        </Flex>
      </Grid>
      {showPhaseBar && phaseBarPhases.length > 0 && (
        <Box paddingX="4px">
          <PhaseBar phases={phaseBarPhases} />
          {scheduleDateRange && (
            <Flex justifyContent="space-between" paddingTop="8px">
              <Text color={GreyGrey60} {...Primary12Regular}>
                {dateTimeWithFormat(scheduleDateRange.startDate, 'd MMM yyyy')}
              </Text>
              <Text color={GreyGrey60} {...Primary12Regular}>
                {formatScheduleEndDate(scheduleDateRange.endDate)}
              </Text>
            </Flex>
          )}
        </Box>
      )}
      <Box
        padding="8px"
        backgroundColor={GreyGrey10}
        borderRadius={BorderRadius6}
      >
        <Totals currency={currency}>
          {groupByPhaseData.map((phase, index) => {
            const orderedPrices = phase.prices

            return (
              <>
                {typeof phase.subTotal === 'undefined' ||
                phase.subTotal === 0 ? (
                  <Totals.Description
                    key={phase.phaseId}
                    title={phase.label}
                    emphasised
                  />
                ) : (
                  <Totals.Row
                    key={phase.phaseId}
                    title={phase.label}
                    amount={phase.subTotal}
                    emphasisedTitle
                  />
                )}

                {orderedPrices.map(price => (
                  <Totals.Row
                    key={price.id}
                    title={price.name}
                    amount={toPriceSummary(price)}
                    formatAmount={false}
                  />
                ))}

                {phase.minimum && (
                  <>
                    <Totals.Description title="Minimum commitment" />

                    {phase.minimum.prices.map(price => (
                      <Flex key={price.id} marginLeft="8px">
                        <Totals.Row
                          key={price.id}
                          title={price.name}
                          amount={toPriceSummary(price)}
                          formatAmount={false}
                        />
                      </Flex>
                    ))}
                  </>
                )}

                {index !== groupByPhaseData.length - 1 && <Totals.Spacer />}
              </>
            )
          })}
        </Totals>
      </Box>
      {action && <Flex>{action}</Flex>}
    </Flex>
  )
}
